import React, { useEffect, useMemo, useState } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useOutletContext,
  Link as RouterLink,
} from "react-router-dom";
import {
  Box,
  Button,
  Link,
  Paper,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { format } from "date-fns-tz";
import { useDispatch, useSelector } from "react-redux";
import { buildValueText } from "../../OrdersList/OrdersList";
import {
  fetchOrderById,
  fetchTicketsForOrder,
  sendOrderConfirmationEmail,
  adminEventsSelector,
  DEFAULT_ORDER_DETAIL,
} from "../../../redux/features/adminEventsSlice";
import { organizationsSliceSelector } from "../../../redux/features/organizationsSlice";
import ResendOrderEmailConfirmationDialog from "../../Admin/Organizations/Orders/ResendOrderEmailConfirmationDialog/ResendOrderEmailConfirmationDialog";
import CancelOrderModal from "./CancelOrderModal";

const getEventIdFromOrder = (order) => {
  if (!order?.event) return null;
  const event = order.event;
  if (typeof event === "string") return event.split("/")[1];
  if (event?.id) return event.id;
  if (event?.path) return event.path.split("/")[1];
  return null;
};

const attendeeIdFromEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

const priceText = (usdCents) => {
  if (usdCents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((usdCents || 0) / 100);
};

const OrderDetails = () => {
  const { eventId: eventIdParam, orderId, organizationId: organizationIdParam } =
    useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { event } = useOutletContext() || {};
  const dispatch = useDispatch();
  const eventsState = useSelector(adminEventsSelector);
  const organizationsState = useSelector(organizationsSliceSelector);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const isCrmOrderView =
    location.pathname.startsWith("/crm/orders/") && organizationIdParam;

  const orderDetail =
    eventsState.orderDetailByOrderId?.[orderId] ?? DEFAULT_ORDER_DETAIL;
  const {
    order: fetchedOrder,
    orderLoading,
    orderError,
    tickets,
    ticketsLoading,
    ticketsError,
  } = orderDetail;

  const organizationId =
    organizationIdParam ??
    event?.organizationId ??
    organizationsState?.currentOrganization?.id;
  const ordersFromEvent = event?.orders;
  const orderFromList = Array.isArray(ordersFromEvent)
    ? ordersFromEvent.find((o) => o.id === orderId)
    : null;

  const order = fetchedOrder ?? orderFromList ?? null;
  const orderReady = !!(orderFromList || fetchedOrder);

  const eventIdFromOrder = order ? getEventIdFromOrder(order) : null;
  const effectiveEventId = eventIdParam ?? eventIdFromOrder;

  useEffect(() => {
    if (!orderId || !organizationId) return;
    if (orderFromList) return;
    dispatch(fetchOrderById({ organizationId, orderId }));
  }, [orderId, organizationId, dispatch, orderFromList]);

  useEffect(() => {
    if (!orderId || !organizationId || !effectiveEventId || !orderReady) return;
    dispatch(
      fetchTicketsForOrder({
        organizationId,
        eventId: effectiveEventId,
        orderId,
      }),
    );
  }, [orderId, organizationId, effectiveEventId, orderReady, dispatch]);

  const ticketAmountsMap = useMemo(() => {
    const orderItems = order?.items ?? [];
    const totalFees = order?.totalFeesUsdCents ?? 0;
    const totalDiscount = order?.promoCodeDiscountUsdCents ?? 0;
    const totalTickets = tickets?.length ?? 0;
    const perTicketFeeCents =
      totalTickets > 0 ? Math.round(totalFees / totalTickets) : 0;
    const perTicketDiscountCents =
      totalTickets > 0 ? Math.round(totalDiscount / totalTickets) : 0;
    const itemByKey = {};
    orderItems.forEach((item) => {
      const key = item.ticketTypeKey;
      if (key != null) itemByKey[key] = item;
    });
    const map = {};
    (tickets || []).forEach((ticket) => {
      const item = itemByKey[ticket.ticketTypeKey];
      let base = 0;
      if (item && item.quantity > 0) {
        base = Math.round(item.totalUsdCents / item.quantity);
      }
      const total = base + perTicketFeeCents - perTicketDiscountCents;
      map[ticket.id] = {
        base,
        fee: perTicketFeeCents,
        discount: perTicketDiscountCents,
        total,
      };
    });
    return map;
  }, [
    order?.items,
    order?.totalFeesUsdCents,
    order?.promoCodeDiscountUsdCents,
    tickets,
  ]);

  if (orderLoading && !order) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const backToOrdersPath = isCrmOrderView
    ? "/crm/orders"
    : eventIdParam
      ? "../orders"
      : "/orders";

  if (!order) {
    return (
      <Box p={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backToOrdersPath)}
          sx={{ mb: 2 }}
        >
          Back to orders
        </Button>
        <Typography color="error">
          {orderError ?? "Order not found."}
        </Typography>
      </Box>
    );
  }

  const {
    id,
    eventName,
    orderFormFields,
    totalUsdCents,
    subtotalUsdCents,
    promoCodeDiscountUsdCents,
    items,
    createdAt,
    promoCode,
    affiliate,
    fees,
    totalFeesUsdCents,
    sessionId,
    sessionName,
    paymentDetails,
    cancellation,
  } = order;

  const datePattern = "MMM d, yyyy 'at' h:mm a";
  const dateText = createdAt?.toDate ? format(createdAt.toDate(), datePattern) : "—";

  const paymentId =
    paymentDetails?.result?.paymentId ?? paymentDetails?.paymentId;
  const captureDetails =
    paymentDetails?.result?.captureDetails ?? paymentDetails?.captureDetails;
  const card = captureDetails?.card ?? {};
  const cardBrand = card.card_brand ?? card.brand;
  const last4 = card.last_4;
  const expMonth = card.exp_month;
  const expYear = card.exp_year;
  const statementDescription =
    captureDetails?.statement_description ?? paymentDetails?.result?.statement_description;
  const orderFormFieldsByName = Object.fromEntries(
    (orderFormFields || []).map((f) => [f.key || f.name, f]),
  );
  const firstLast = [
    orderFormFieldsByName.firstName?.value,
    orderFormFieldsByName.lastName?.value,
  ]
    .filter(Boolean)
    .join(" ");
  const purchaserName =
    orderFormFieldsByName.name?.value ?? (firstLast || null);
  const purchaserEmail = orderFormFieldsByName.email?.value ?? null;

  const instances = cancellation?.instances ?? [];

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <ResendOrderEmailConfirmationDialog
        open={resendDialogOpen}
        handleCancel={() => setResendDialogOpen(false)}
        handleConfirm={async ({ sendAttendees }) => {
          await dispatch(
            sendOrderConfirmationEmail({
              orderId: id,
              organizationId,
              sendAttendees,
            }),
          );
          setResendDialogOpen(false);
        }}
      />
      <CancelOrderModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        order={order}
        event={event}
        eventId={effectiveEventId}
        organizationId={organizationId}
        ticketsForOrder={tickets}
        onSuccess={() => {
          setCancelModalOpen(false);
          if (!orderId || !organizationId || !effectiveEventId) return;
          dispatch(fetchOrderById({ organizationId, orderId }));
          dispatch(
            fetchTicketsForOrder({
              organizationId,
              eventId: effectiveEventId,
              orderId,
            }),
          );
        }}
      />
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(backToOrdersPath)}
        sx={{ mb: 2 }}
      >
        Back to orders
      </Button>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">
            Order {id}
          </Typography>
          {cancellation?.status === "full" && (
            <Typography variant="body2" color="error">
              Cancelled
            </Typography>
          )}
          {cancellation?.status === "partial" && (
            <Typography variant="body2" sx={{ color: "warning.main" }}>
              Partially cancelled
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setResendDialogOpen(true)}
          >
            Resend email
          </Button>
          {effectiveEventId && !isCrmOrderView && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => setCancelModalOpen(true)}
            >
              Cancel / refund
            </Button>
          )}
        </Stack>
      </Box>
      {effectiveEventId && !eventIdParam && !isCrmOrderView ? (
        <Link
          component={RouterLink}
          to={`/events/${effectiveEventId}/dashboard`}
          variant="body2"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {eventName}
        </Link>
      ) : (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {eventName}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        {dateText}
      </Typography>

      {sessionName && sessionId && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Session: {sessionName}
        </Typography>
      )}
      {promoCode && (
        <Typography variant="body2">Promo code: {promoCode}</Typography>
      )}
      {affiliate && (
        <Typography variant="body2">Affiliate: {affiliate}</Typography>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Order totals
        </Typography>
        <Box
          component="dl"
          sx={{
            m: 0,
            maxWidth: 280,
            "& > div": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 2,
              mb: 0.5,
            },
            "& dd": { m: 0, textAlign: "right" },
          }}
        >
          {subtotalUsdCents != null && subtotalUsdCents !== totalUsdCents && (
            <Box component="div">
              <Typography component="dt" variant="body2">
                Subtotal:
              </Typography>
              <Typography component="dd" variant="body2">
                {priceText(subtotalUsdCents)}
              </Typography>
            </Box>
          )}
          {promoCodeDiscountUsdCents > 0 && (
            <Box component="div">
              <Typography component="dt" variant="body2">
                Discount:
              </Typography>
              <Typography component="dd" variant="body2">
                −{priceText(promoCodeDiscountUsdCents)}
              </Typography>
            </Box>
          )}
          {totalFeesUsdCents > 0 && (
            <Box component="div">
              <Typography component="dt" variant="body2">
                Fees:
              </Typography>
              <Typography component="dd" variant="body2">
                {priceText(totalFeesUsdCents)}
              </Typography>
            </Box>
          )}
          <Box component="div">
            <Typography component="dt" variant="body2" fontWeight="bold">
              Total:
            </Typography>
            <Typography component="dd" variant="body2" fontWeight="bold">
              {priceText(totalUsdCents)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {fees && fees.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Fee breakdown
          </Typography>
          {fees.map((fee, i) => (
            <Typography key={i} variant="body2">
              {fee.name}: {priceText(fee.amount)}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Purchaser information
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          {(orderFormFields || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No purchaser form responses
            </Typography>
          ) : (
            (orderFormFields || []).map((field, i) => (
              <Box key={`order-field-${field.key ?? i}-${i}`} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="medium" component="span">
                  {field.name}:{" "}
                </Typography>
                <Typography variant="body2" component="span">
                  {buildValueText(field.value, field.type)}
                </Typography>
              </Box>
            ))
          )}
        </Paper>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Order summary
        </Typography>
        {(items || []).map((item, i) => (
          <Typography key={i} variant="body2">
            {item.quantity} × {item.name}
          </Typography>
        ))}
      </Box>

      {paymentId && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Square payment
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderLeft: "4px solid", borderColor: "success.main" }}>
            <Typography variant="body2">
              <strong>Payment ID:</strong> {paymentId}
            </Typography>
            {(cardBrand || last4) && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Card:</strong>{" "}
                {[cardBrand, last4 ? `•••• ${last4}` : null]
                  .filter(Boolean)
                  .join(" — ")}
                {expMonth != null && expYear != null && (
                  <span> (exp {String(expMonth).padStart(2, "0")}/{expYear})</span>
                )}
              </Typography>
            )}
            {statementDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>Statement:</strong> {statementDescription}
              </Typography>
            )}
            {(purchaserName || purchaserEmail) && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Purchaser:</strong>{" "}
                {purchaserName && <span>{purchaserName}</span>}
                {purchaserName && purchaserEmail && " — "}
                {purchaserEmail &&
                  (organizationId ? (
                    <Link
                      component={RouterLink}
                      to={`/organizations/${organizationId}/attendees/${attendeeIdFromEmail(purchaserEmail)}`}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: "primary.main",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {purchaserEmail}
                    </Link>
                  ) : (
                    <span>{purchaserEmail}</span>
                  ))}
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {instances.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Refunds / cancellations
          </Typography>
          {instances.map((inst, idx) => {
            const ticketsList = inst.tickets ?? [];
            const totalRefundCents = inst.refundTotalUsdCents ?? 0;
            const sq = inst.squareRefund;
            const refundAt =
              inst.at?.toDate?.() ??
              (inst.at instanceof Date ? inst.at : null);
            const refundDateText = refundAt
              ? format(refundAt, datePattern)
              : "—";
            const rowSx = { mb: 1 };
            return (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1, borderLeft: "4px solid", borderColor: "error.main" }}>
                <Typography variant="body2" sx={rowSx}>
                  <strong>Date:</strong> {refundDateText}
                </Typography>
                {inst.cancellationReason && (
                  <Typography variant="body2" sx={rowSx}>
                    <strong>Reason:</strong> {inst.cancellationReason}
                  </Typography>
                )}
                {ticketsList.length > 0 && (
                  <Typography variant="body2" sx={rowSx}>
                    <strong>Cancelled:</strong> {ticketsList.length} ticket
                    {ticketsList.length === 1 ? "" : "s"}
                  </Typography>
                )}
                <Typography variant="body2" sx={rowSx}>
                  <strong>Refunded:</strong> {priceText(totalRefundCents)}
                  {last4 && totalRefundCents > 0 && (
                    <span> to card ending •••• {last4}</span>
                  )}
                </Typography>
                {sq?.refundId && (
                  <Typography variant="body2" sx={rowSx}>
                    <strong>Square refund ID:</strong> {sq.refundId}
                  </Typography>
                )}
                {sq?.error && (
                  <Typography variant="body2" color="error" sx={{ mb: 0 }}>
                    Square refund error: {sq.error}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Tickets
        </Typography>
        {ticketsLoading && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading tickets…</Typography>
          </Box>
        )}
        {ticketsError && (
          <Typography variant="body2" color="error">
            {ticketsError}
          </Typography>
        )}
        {!ticketsLoading && !ticketsError && tickets && tickets.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tickets found for this order
          </Typography>
        )}
        {!ticketsLoading && !ticketsError && tickets && tickets.length > 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            {tickets.map((ticket, idx) => {
              const isCancelled = Boolean(ticket.cancellation);
              const of = ticket.orderFormFields ?? [];
              const ticketAmountUsdCents = ticketAmountsMap[ticket.id];
              return (
                <Paper
                  key={ticket.id ?? idx}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderLeft: isCancelled ? "4px solid" : undefined,
                    borderColor: isCancelled ? "error.main" : undefined,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="600"
                      color={isCancelled ? "error.main" : undefined}
                    >
                      {ticket.name ?? "Ticket"} {isCancelled && "(cancelled)"}
                    </Typography>
                    {ticketAmountUsdCents != null && (
                      <Box
                        sx={{
                          textAlign: "right",
                          "& > div": {
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            alignItems: "baseline",
                          },
                          "& .amount": { minWidth: 56 },
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ mr: 1 }}
                          >
                            Price:
                          </Typography>
                          <Typography variant="body2" className="amount">
                            {priceText(ticketAmountUsdCents.base)}
                          </Typography>
                        </Box>
                        {ticketAmountUsdCents.fee > 0 && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="span"
                              sx={{ mr: 1 }}
                            >
                              Fees:
                            </Typography>
                            <Typography variant="body2" className="amount">
                              {priceText(ticketAmountUsdCents.fee)}
                            </Typography>
                          </Box>
                        )}
                        {ticketAmountUsdCents.discount > 0 && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="span"
                              sx={{ mr: 1 }}
                            >
                              Discount:
                            </Typography>
                            <Typography variant="body2" className="amount">
                              −{priceText(ticketAmountUsdCents.discount)}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            component="span"
                            sx={{ mr: 1 }}
                          >
                            Total:
                          </Typography>
                          <Typography variant="body2" fontWeight="600" className="amount">
                            {priceText(ticketAmountUsdCents.total)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {of.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No form responses
                    </Typography>
                  )}
                  {of.map((f, i) => (
                    <Box key={`of-${f.key ?? 'f'}-${i}`} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium" component="span">
                        {f.name}:{" "}
                      </Typography>
                      {f.key === "email" &&
                      organizationId &&
                      typeof f.value === "string" &&
                      f.value.trim() ? (
                        <Typography variant="body2" component="span">
                          <Link
                            component={RouterLink}
                            to={`/organizations/${organizationId}/attendees/${attendeeIdFromEmail(f.value)}`}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: "primary.main",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {buildValueText(f.value, f.type)}
                          </Link>
                        </Typography>
                      ) : (
                        <Typography variant="body2" component="span">
                          {buildValueText(f.value, f.type)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OrderDetails;
